import { Box, Typography } from '@mui/material';
import { TrendingUpOutlined as PromoteIcon } from '@mui/icons-material';

import { FBoxContainer, FHButton, FHSelect } from '../../../../components/base';
import { ProviderClinicalStatus, ProviderClinicalStatusReason } from '../../../constants/general';
import { useProviderClinicalActions } from '../hooks';
import { getOptions } from '../utils';
import { CLINICAL_STATUS_SELECT_SX } from '../styles';
import { PromoteToClinicalDialog } from './PromoteToClinicalDialog';

import type { SelectValueType } from '../../../../utils/types';

export function ProviderClinicalStatusUpdate() {
  const {
    provider,
    clinicalProvider,
    isLoading,
    selectedClinicalStatus,
    selectedClinicalStatusReason,
    showPromoteToClinical,
    selectedRoles,
    setSelectedClinicalStatus,
    setSelectedClinicalStatusReason,
    setSelectedRoles,
    handleUpdateClinicalStatus,
    handlePromoteToClinicalClick,
    handlePromoteProvider,
    closePromoteDialog,
  } = useProviderClinicalActions();

  const requiresReason =
    selectedClinicalStatus === ProviderClinicalStatus.OFFBOARDED ||
    selectedClinicalStatus === ProviderClinicalStatus.LIMITED_ACCESS_GUARDIAN;

  const handleClinicalStatusChange = (value: SelectValueType) => {
    setSelectedClinicalStatus(String(value));
    setSelectedClinicalStatusReason('');
  };

  const handleClinicalStatusReasonChange = (value: SelectValueType) => {
    setSelectedClinicalStatusReason(String(value));
  };

  return (
    <>
      <FBoxContainer sx={{ height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" mb={2}>
              Update clinical status
            </Typography>
            {!clinicalProvider &&
              !isLoading &&
              provider?.clinical_status !== ProviderClinicalStatus.GUARDIAN_INVITED && (
                <FHButton onClick={handlePromoteToClinicalClick} startIcon={<PromoteIcon />}>
                  Promote to clinical
                </FHButton>
              )}
          </Box>

          <Typography mt={2} mr={1} fontWeight={700} display="inline-block">
            Current:
          </Typography>
          <Typography display="inline-block">{provider?.clinical_status ?? ''}</Typography>

          <Box sx={{ mt: 3 }}>
            <Typography mb={1} fontWeight={700}>
              Change to:
            </Typography>
            <FHSelect
              sx={CLINICAL_STATUS_SELECT_SX}
              inputHeight={40}
              options={getOptions(provider?.clinical_status)}
              value={selectedClinicalStatus}
              onUpdate={handleClinicalStatusChange}
            />
          </Box>

          {requiresReason && (
            <Box sx={{ mt: 3 }}>
              <Typography mb={1} fontWeight={700}>
                Reason:
              </Typography>
              <FHSelect
                sx={CLINICAL_STATUS_SELECT_SX}
                inputHeight={40}
                disabled={!selectedClinicalStatus}
                options={Object.values(ProviderClinicalStatusReason).map((val) => ({
                  value: val,
                  label: val,
                }))}
                value={selectedClinicalStatusReason}
                onUpdate={handleClinicalStatusReasonChange}
              />
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FHButton
            onClick={handleUpdateClinicalStatus}
            sx={{ mt: 3, width: '100px' }}
            disabled={!selectedClinicalStatus || (requiresReason && !selectedClinicalStatusReason)}
          >
            Update
          </FHButton>
        </Box>
      </FBoxContainer>
      {!clinicalProvider && (
        <PromoteToClinicalDialog
          open={showPromoteToClinical}
          selectedRoles={selectedRoles}
          onRolesChange={setSelectedRoles}
          onClose={closePromoteDialog}
          onSubmit={handlePromoteProvider}
        />
      )}
    </>
  );
}
